/**
 * Pure, presentation-free helpers shared across the Campaign Builder editors.
 *
 * These were hand-copied into most of the builder's list/field editors (AssetsEditor, OutcomesEditor,
 * CapabilitiesEditor, NestedContentEditor, StepFilesFields, StepQuestionsFields, SchemaPayloadFields).
 * They are ordinary functions with no React or MUI dependency, so collecting them here removes the
 * duplication without touching either of the builder's two form models (react-hook-form vs.
 * commit-on-blur flat payload). No behaviour changes: each definition is the verbatim one the editors
 * already used.
 */

/** The next `order` for a new row appended to `rows`: one past the current max, or 0 when empty. */
export const nextOrderOf = (rows: Array<{ order?: number }>): number =>
  rows.length ? Math.max(...rows.map((r) => r.order ?? 0)) + 1 : 0;

/** Rows sorted by `order` ascending, breaking ties by `id` so the order is stable across renders. */
export const byOrder = <T extends { order?: number; id: number }>(rows: T[]): T[] =>
  [...rows].sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.id - b.id);

/**
 * A short signature of the server rows' identity + order, so a local mirror only reseeds when the
 * server list actually changes (not on every render) — otherwise in-progress typing would be clobbered.
 */
export const signature = (rows: Array<{ id: number; order?: number }>): string =>
  byOrder(rows)
    .map((r) => `${r.id}:${r.order ?? 0}`)
    .join('|');

/** Split a "one item per line" textarea into a trimmed, blank-dropped string array. */
export const linesToArr = (text: string): string[] =>
  text
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);

/** Render a string array back into a "one item per line" textarea value; anything non-array → ''. */
export const arrToLines = (v: unknown): string => (Array.isArray(v) ? (v as string[]).join('\n') : '');
